/**
 * guideContentTypes — B-2026-0088 Phase A1
 *
 * Unified type definitions for structured guide content.
 *
 * `StructuredGuideBody` replaces single-paragraph strings with a lead sentence
 * plus bullet points, allowing the overlay to render bite-sized scannable content
 * instead of a wall of text.
 *
 * `GuideEntry` supersedes the per-surface entry types — both eval and studio
 * entries can use this shape. Legacy `string` body is still valid for backward
 * compatibility.
 *
 * `resolveGuideBody()` always returns a `StructuredGuideBody`; wrapping a plain
 * string as `{ lead: str, points: [] }` means downstream renderers only need
 * one code path.
 */

// ---------------------------------------------------------------------------
// EvalSummary — moved here from evaluationGuideContent to avoid circular deps
// ---------------------------------------------------------------------------

/** Minimal evaluation summary threaded to body resolver functions. */
export interface EvalSummary {
  overallScore?: number;
  phases?: Array<{
    phase_label?: string;
    phase_name?: string;
    form_score?: number;
    spatial_score?: number;
    phase_weight?: number;
  }>;
  hasGoals?: boolean;
  hasGemini?: boolean;
  mirrored?: boolean;
  automaticFail?: boolean;
}

// ---------------------------------------------------------------------------
// Core types
// ---------------------------------------------------------------------------

/**
 * Structured body for a guide entry.
 *
 * - `lead`      — one sentence (~15 words) summarising the topic.
 * - `points`    — 2-4 bullet points (~10 words each).
 * - `lookFor`   — optional call-to-action pointing at the on-screen element.
 * - `learnMoreUrl` — optional doc link (overrides entry-level learnMoreUrl).
 */
export interface StructuredGuideBody {
  lead: string;
  points: string[];
  lookFor?: string;
  learnMoreUrl?: string;
}

/** Union body type: structured object, legacy string, or a runtime resolver. */
export type GuideBodyValue =
  | string
  | StructuredGuideBody
  | ((eval_: EvalSummary) => string | StructuredGuideBody);

/**
 * Unified guide entry used by both evaluation and studio content registries.
 *
 * - `anchorId` — optional guide key identifying which on-screen element this
 *   entry describes (used by the anchor highlight system in Phase A3).
 */
export interface GuideEntry {
  heading: string;
  body: GuideBodyValue;
  anchorId?: string;
  learnMoreUrl?: string;
}

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

export function isStructuredBody(v: unknown): v is StructuredGuideBody {
  return (
    typeof v === 'object' &&
    v !== null &&
    'lead' in v &&
    'points' in v &&
    Array.isArray((v as StructuredGuideBody).points)
  );
}

// ---------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------

/**
 * Resolve a `GuideBodyValue` into a `StructuredGuideBody`.
 *
 * - If the body is already a `StructuredGuideBody`, return it directly.
 * - If the body is a resolver function, call it with `eval_` and wrap the
 *   result as needed.
 * - If the body is a plain string (legacy), wrap as `{ lead, points: [] }`.
 *
 * Never throws — falls back to an empty lead if the resolver errors.
 */
export function resolveGuideBody(
  entry: { body: GuideBodyValue; learnMoreUrl?: string },
  eval_?: EvalSummary,
): StructuredGuideBody {
  let raw: string | StructuredGuideBody;

  if (typeof entry.body === 'function') {
    try {
      raw = entry.body(eval_ ?? {});
    } catch {
      raw = '';
    }
  } else {
    raw = entry.body;
  }

  if (isStructuredBody(raw)) {
    // Carry through entry-level learnMoreUrl if not overridden on the body.
    if (!raw.learnMoreUrl && entry.learnMoreUrl) {
      return { ...raw, learnMoreUrl: entry.learnMoreUrl };
    }
    return raw;
  }

  // Wrap legacy string: keep the full text as the lead, no bullets.
  return {
    lead: String(raw),
    points: [],
    learnMoreUrl: entry.learnMoreUrl,
  };
}
